import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FolderGit2,
  Settings,
  Upload,
  CreditCard,
  LayoutList,
  BadgeCheck,
  BookOpen,
  Terminal,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";
import { Button } from "../shadcn/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "../shadcn/sheet";
import { Separator } from "../shadcn/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../shadcn/tooltip";
import { ScrollArea } from "../shadcn/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../shadcn/dropdown-menu";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { label: "Repositories", href: "/dashboard/repos", icon: FolderGit2 },
  { label: "Assurance", href: "/assurance", icon: BadgeCheck },
  { label: "Guide", href: "/guide", icon: BookOpen },
  { label: "API Docs", href: "/api-docs", icon: Terminal },
  { label: "Upload Code", href: "/upload", icon: Upload },
  { label: "Billing", href: "/billing", icon: CreditCard },
  { label: "Subscriptions", href: "/subscription-management", icon: LayoutList },
  { label: "Settings", href: "/settings", icon: Settings },
];

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(href);
  };

  // Shared nav item renderer
  const NavItemLink = ({
    item,
    onNavigate,
    showLabel,
  }: {
    item: NavItem;
    onNavigate?: () => void;
    showLabel: boolean;
  }) => {
    const active = isActive(item.href);
    const Icon = item.icon;

    const link = (
      <Button
        variant="ghost"
        asChild
        className={cn(
          "relative w-full justify-start gap-3 rounded-lg px-3 py-1.5 h-auto text-sm font-medium transition-colors",
          active
            ? "bg-surface2/80 text-text border-l-2 border-brand"
            : "text-muted hover:text-text hover:bg-surface2/50 border-l-2 border-transparent"
        )}
      >
        <Link to={item.href} onClick={onNavigate}>
          <Icon className="h-[18px] w-[18px] flex-shrink-0" />
          {showLabel && <span>{item.label}</span>}
        </Link>
      </Button>
    );

    // When collapsed on desktop, wrap with tooltip
    if (!showLabel) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return link;
  };

  // User avatar (first letter fallback)
  const UserAvatar = ({ size = "h-8 w-8" }: { size?: string }) => {
    const displayName = user?.github_name || user?.github_login || "?";
    const initial = displayName.charAt(0).toUpperCase();

    if (user?.github_avatar_url) {
      return (
        <img
          src={user.github_avatar_url}
          alt={user.github_login}
          className={cn(size, "rounded-lg object-cover")}
        />
      );
    }

    return (
      <div
        className={cn(
          size,
          "flex items-center justify-center rounded-lg bg-brand/15 text-brand text-sm font-semibold"
        )}
      >
        {initial}
      </div>
    );
  };

  // User section with dropdown
  const UserSection = ({ showDetails }: { showDetails: boolean }) => {
    if (!user) {
      return (
        <Button asChild className="w-full justify-center">
          <Link to="/login">
            {showDetails ? "Sign In" : <LogOut className="h-4 w-4" />}
          </Link>
        </Button>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-surface2/50",
              !showDetails && "justify-center"
            )}
          >
            <UserAvatar />
            {showDetails && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-text">
                  {user.github_name || user.github_login}
                </div>
                <div className="truncate text-xs text-muted">
                  @{user.github_login}
                </div>
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={showDetails ? "top" : "right"}
          align="start"
          className="w-56"
        >
          <DropdownMenuLabel>
            <div className="truncate text-sm font-semibold">
              {user.github_name || user.github_login}
            </div>
            <div className="truncate text-xs font-normal text-muted">
              @{user.github_login}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={logout}
            className="cursor-pointer text-danger focus:text-danger"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // Sidebar content shared between desktop and mobile
  const SidebarNav = ({
    showLabels,
    onNavigate,
  }: {
    showLabels: boolean;
    onNavigate?: () => void;
  }) => (
    <div className="flex h-full flex-col">
      {/* Brand: full product name with the logo on the right */}
      <div className="flex items-center gap-2.5 px-4 py-5">
        {showLabels && (
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-base font-bold tracking-tight text-text">
              Paxley
            </span>
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted">
              Code Security Platform
            </span>
          </span>
        )}
        <img
          src="/logo-mark.png"
          alt="Paxley Code Security Platform"
          className={`h-9 w-9 flex-shrink-0 rounded-lg object-contain ${showLabels ? "ml-auto" : ""}`}
        />
      </div>

      <Separator className="bg-border/40" />

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav>
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.href}>
                <NavItemLink
                  item={item}
                  onNavigate={onNavigate}
                  showLabel={showLabels}
                />
              </li>
            ))}
          </ul>
        </nav>
      </ScrollArea>

      {/* Bottom section */}
      <div className="mt-auto">
        <Separator className="bg-border/40" />

        {/* Collapse toggle (desktop only, hidden on mobile) */}
        <div className="hidden md:flex justify-center py-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="h-8 w-8 text-muted hover:text-text"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        <Separator className="hidden md:block bg-border/40" />

        {/* User */}
        <div className="p-3">
          <UserSection showDetails={showLabels} />
        </div>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile: Sheet trigger + drawer */}
      <div className="md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="fixed left-4 top-4 z-50 bg-surface1/90 backdrop-blur-lg ring-1 ring-border/50 shadow-lg"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-72 p-0 bg-surface1 border-border/40"
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarNav
              showLabels
              onNavigate={() => setMobileOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden md:flex sticky top-0 h-dvh flex-col bg-surface1 border-r border-border/40 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
          collapsed ? "w-16" : "w-60"
        )}
      >
        <SidebarNav showLabels={!collapsed} />
      </aside>
    </TooltipProvider>
  );
}
